import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import TranslationProvider, { getTranslation } from '../../../../providers/TranslationProvider';
import Button from '../../../Button';
import AccountPaymentMethodRow from '../AccountPaymentMethodRow';
import AccountRemoveCard from '../AccountRemoveCard';
import AccountRemoveError from '../AccountRemoveError';
import ErrorModal from '../../../ErrorModal';
import Loader from '../../../Loader';
import Modal from '../../../Modal';
import AriaLiveRegion from '../../../AriaLiveRegion';
import '../../AccountPage.scss';
import './AccountPaymentMethods.scss';
import APP_CONTEXT from '../../../../constants/appContext';
import { STRIPE_FORM_CONTEXT } from '../../../../constants/paymentProcessors';
import StripeForm from '../../../StripeForm/StripeForm';
import { loadResyConfigThunk } from '../../../../reducers';
import { IDLE } from '../../../../constants/asyncLoadingStatuses';
import { deleteUserPaymentMethod, getUser, postUserPaymentMethod } from '../../../../api';
import { handleNavigate } from '../../../../utils/locationUtils';
import RouterNavigateContext from '../../../../../../widget/app/modules/contexts/RouterNavigate';
import AnnouncementCard from '../../../AnnouncementCard';
import { getAnnouncements } from '../../../../../../consumer_web/app/modules/containers/AnnouncementContainer/AnnouncementAPI';
import { Logger, openExternalUrl } from '../../../../utils';
import { EVENT_TYPE } from '../../../Announcement/announcementTypes';
import Icon from '../../../Icon';
import ProTipIcon from '../../../../svg/icons/ic-protip.svg';

const MODAL_ADD_CARD = 'add-card';
const MODAL_CONFIRM_REMOVE = 'confirm-remove';
const MODAL_GDA_MESSAGING = 'gda-messaging';
const MODAL_REMOVE_FAILED = 'remove-failed';

const PAGE = 'payment_page';
const ANNOUNCEMENT_TYPE = 'tile';

const sortByDefault = (a, b) => {
  if (a.is_default) {
    return -1;
  }
  if (b.is_default) {
    return 1;
  }
  return 0;
};

const sortPaymentMethods = (paymentMethods) => paymentMethods.sort(sortByDefault);

const accountPaymentMethodsPropTypes = {
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  context: PropTypes.string.isRequired,
  AutomationMode: PropTypes.bool,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  flushRoute: PropTypes.func,
  isGda: PropTypes.bool,
  modalType: PropTypes.string,
  configStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  stripePublishableKey: PropTypes.string,
  deviceId: PropTypes.string,
  locationCode: PropTypes.string,
};

class AccountPaymentMethods extends Component {
  static propTypes = accountPaymentMethodsPropTypes;

  static contextType = RouterNavigateContext;

  static defaultProps = {
    AutomationMode: false,
    flushRoute: () => {},
    isGda: false,
    modalType: null,
    stripePublishableKey: undefined,
    deviceId: null,
    locationCode: null,
  }

  state = {
    paymentMethods: [],
    loading: true,
    modalType: null,
    announcement: null,
    paymentPageAnnouncement: null,
  };

  componentDidMount() {
    const {
      configStatus, deviceId, locationCode, dispatch,
    } = this.props;
    if (configStatus === IDLE) {
      dispatch(loadResyConfigThunk());
    }
    this.loadCards();

    const { modalType } = this.props;
    if (modalType === MODAL_ADD_CARD) {
      this.handleAddCard();
    }

    if (deviceId && locationCode) {
      this.fetchAnnouncement();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { deviceId: prevDeviceId, locationCode: prevLocationCode } = prevProps;
    const { deviceId, locationCode } = this.props;
    if (prevDeviceId !== deviceId || prevLocationCode !== locationCode) {
      this.fetchAnnouncement();
    }

    const { paymentPageAnnouncement: prevAnnouncement } = prevState;
    const { paymentPageAnnouncement } = this.state;
    if (paymentPageAnnouncement?.id && prevAnnouncement?.id !== paymentPageAnnouncement?.id) {
      if (window.amplitude) {
        this.trackViewAnnouncementEvent();
      } else {
        window.addEventListener('ResyAmplitudeLoaded', this.trackViewAnnouncementEvent);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('ResyAmplitudeLoaded', this.trackViewAnnouncementEvent);
  }

  trackViewAnnouncementEvent = () => {
    const { paymentPageAnnouncement } = this.state;
    this.trackEvent(EVENT_TYPE.VIEW_ANNOUNCEMENT, paymentPageAnnouncement);
  };

  loadCards = () => {
    this.loadingPromise = getUser()
      .then((user) => {
        const paymentMethods = sortPaymentMethods(user.payment_methods || []);
        this.setState({
          paymentMethods,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({
          paymentMethods: [],
          loading: false,
          error: 'load',
        });
      });
  };

  handleDefaultCard = async (paymentMethod) => {
    const { context } = this.props;
    const cardNumber = paymentMethod?.display;
    postUserPaymentMethod({ payment_method_id: paymentMethod.id })
      .then(() => {
        const { paymentMethods } = this.state;
        this.setState({
          announcement: getTranslation({
            translationKey: 'default_credit_card_set',
            translationValues: { cardNumber },
            fallback: `Card number ending with ${cardNumber} is set as default`,
          }),
          paymentMethods: paymentMethods.map((row) => ({
            ...row,
            is_default: (row.id === paymentMethod.id),
          })),
        });
        /* istanbul ignore else */
        if (context === 'widget') {
          window.parent.postMessage('onProfileSync', '*');
        }
      });
  }

  handleRemoveCardOnComplete = (paymentMethod) => {
    const { context } = this.props;
    this.closeModal();
    deleteUserPaymentMethod(`${paymentMethod.id}`)
      .then(() => {
        this.loadCards();
        this.setState({
          announcement: getTranslation({
            translationKey: 'credit_card_removed',
            translationValues: { cardNumber: paymentMethod.display },
            fallback: `Card ending with ${paymentMethod.display} is removed`,
          }),
        });
        /* istanbul ignore else */
        if (context === 'widget') {
          window.parent.postMessage('onProfileSync', '*');
        }
      })
      .catch((err) => {
        const isDueToReservation = err && err.status === 409;
        this.setState({
          modalType: MODAL_REMOVE_FAILED,
          modalProps: { isDueToReservation },
        });
      });
  }

  handleRemoveCard = (paymentMethod) => {
    this.setState({
      modalType: MODAL_CONFIRM_REMOVE,
      modalProps: {
        onComplete: () => this.handleRemoveCardOnComplete(paymentMethod),
      },
    });
  }

  handleAddCard = () => {
    const {
      Analytics,
      AutomationMode,
      flushRoute,
      isGda,
    } = this.props;
    const navigate = this.context;

    this.setState({
      modalType: MODAL_ADD_CARD,
      modalProps: {
        Analytics,
        AutomationMode,
        isGda,
        onGdaMessaging: this.onGdaMessaging,
        onComplete: (paymentMethods) => {
          this.setState({
            modalType: null,
            modalProps: null,
            paymentMethods: paymentMethods.sort(sortByDefault),
          });
          handleNavigate('account/payment-methods', navigate, flushRoute);
        },
      },
    });
  }

  closeModal = () => {
    this.setState({
      modalType: null,
      modalProps: null,
    });
  };

  refreshPage = () => {
    window.location.reload();
  }

  getGdaModalState = (message, title) => (
    {
      modalType: MODAL_GDA_MESSAGING,
      modalProps: {
        message,
        onClose: this.closeModal,
        title,
        primaryCTAKey: 'gda_try_again',
        primaryCTAText: 'Try Again',
        primaryCTAOnClick: this.handleAddCard,
        primaryCTAVariant: 'secondary',
        secondaryCTAHref: '/global-dining-access',
        secondaryCTAAdditionalProps: { target: '_blank' },
        secondaryCTAKey: 'gda_more_info',
        secondaryCTAText: 'More Info',
        secondaryCTAOnClick: () => {},
        secondaryCTAVariant: 'button-primary',
      },
    }
  );

  onGdaMessaging = (data) => {
    const {
      result,
      payment_methods: paymentMethods,
    } = data;

    const {
      title,
      message,
    } = result;

    this.setState(
      {
        ...this.getGdaModalState(message, title),
        paymentMethods: paymentMethods.sort(sortByDefault),
      }
    );
  }

  renderAnnouncementMessage = () => {
    const { announcement } = this.state;
    return (
      <>
        {announcement}
      </>
    );
  }

  renderCloseButton = () => (
    <Button
      variant="close"
      aria-label={getTranslation({ translationKey: 'close_payment', fallback: 'Close payment' })}
      onClick={this.closeModal}
    />
  );

  getAddCardModal = () => {
    const {
      Analytics, matchMedia, AutomationMode, context, stripePublishableKey,
    } = this.props;

    const addCreditCardLabel = getTranslation({ translationKey: 'add_credit_card', fallback: 'Add Credit Card' });

    return (
      <Modal
        className="AccountPaymentMethods__add-card"
        isOpen
        onRequestClose={this.closeModal}
        aria={{
          label: addCreditCardLabel,
        }}
        styles={{
          content: {
            padding: '40px 0 80px 0',
            margin: '40px auto',
            maxWidth: '700px',
          },
        }}
      >
        {this.renderCloseButton()}
        <StripeForm
          Analytics={Analytics}
          AutomationMode={AutomationMode}
          matchMedia={matchMedia}
          redirectPath={context === 'widget' ? '/#/account/payment-methods' : '/account/payment-methods'}
          closeForm={this.refreshPage}
          context={STRIPE_FORM_CONTEXT.profile}
          stripePublishableKey={stripePublishableKey}
        />
      </Modal>
    );
  }

  async fetchAnnouncement() {
    const { deviceId, locationCode } = this.props;
    try {
      const response = await getAnnouncements({ deviceId, locationCode, flowContext: PAGE });
      if (response?.length) {
        const data = response.find((item) => item.display?.web?.type === ANNOUNCEMENT_TYPE);
        this.setState({ paymentPageAnnouncement: data });
      }
    } catch (error) {
      Logger.error('[AcountPaymentAnnouncement]: error fetching announcements', error);
    }
  }

  trackEvent(eventName, data) {
    const { Analytics } = this.props;
    Analytics.event(eventName, {
      context: 'payment methods',
      type: ANNOUNCEMENT_TYPE,
      title: data?.display?.title,
      id: data?.id,
    });
  }

  renderModal() {
    const { modalType, modalProps } = this.state;

    switch (modalType) {
      case MODAL_CONFIRM_REMOVE:
        return (
          <Modal
            isOpen
            onRequestClose={this.closeModal}
            contentLabel={getTranslation({ translationKey: 'remove_stored_card_question', fallback: 'Remove Stored Card?' })}
          >
            <AccountRemoveCard onAbort={this.closeModal} {...modalProps} />
          </Modal>
        );

      case MODAL_ADD_CARD:
        return this.getAddCardModal();
      case MODAL_GDA_MESSAGING:
        return (
          <ErrorModal {...modalProps} />
        );
      case MODAL_REMOVE_FAILED:
        return (
          <Modal
            isOpen
            onRequestClose={this.closeModal}
            contentLabel={getTranslation({ translationKey: 'remove_card_error_title', fallback: 'Unable to Remove Card' })}
          >
            <AccountRemoveError onAbort={this.closeModal} {...modalProps} />
          </Modal>
        );
      default:
        return null;
    }
  }

  renderAnnouncementTile() {
    const { paymentPageAnnouncement } = this.state;

    const onClick = () => {
      if (paymentPageAnnouncement?.action?.data?.url) {
        this.trackEvent(EVENT_TYPE.CLICK_ANNOUNCEMENT, paymentPageAnnouncement);
        openExternalUrl(paymentPageAnnouncement.action.data.url);
      }
    };

    return (
      <>
        {paymentPageAnnouncement ? (
          <div className="AccountPaymentMethods-announcement-container">
            <div className="AccountPaymentMethods-protip-container">
              <Icon
                component={ProTipIcon}
                role="img"
                iconClass="protip"
                width={20}
                height={20}
                ariaLabel={getTranslation({ translationKey: 'protip_icon', fallback: 'Pro tip icon' })}
              />
              <TranslationProvider translationKey="protip_text">
                Pro tip: add your eligible American Express&reg; Card to earn dining rewards and make the most of Resy. Terms Apply.
              </TranslationProvider>
            </div>
            <AnnouncementCard
              imageUrl={paymentPageAnnouncement.display?.image}
              header={paymentPageAnnouncement.display?.title}
              iconUrl={paymentPageAnnouncement.display?.lockup_image}
              onClickCTA={onClick}
            />
          </div>
        ) : null}
      </>
    );
  }

  render() {
    const { context } = this.props;
    const {
      loading,
      paymentMethods,
      error,
      modalType,
    } = this.state;

    let content = (
      <div className="AccountPaymentMethods">
        <div className="AccountPaymentMethods__container">
          <Loader className="AccountPage__loader" />
        </div>
      </div>
    );
    const hasPaymentMethods = paymentMethods.length > 0;

    if (!loading) {
      content = (
        <div className="AccountPaymentMethods">
          <AriaLiveRegion id="payment_method_announcement">
            { this.renderAnnouncementMessage() }
          </AriaLiveRegion>
          <div className="AccountPaymentMethods__container">
            {!hasPaymentMethods && (
            <div className="AccountPaymentMethods__no-payment-methods">
              <div>
                <TranslationProvider translationKey="no_payment_methods">
                  You have no payment methods on file.
                </TranslationProvider>
              </div>
            </div>
            )}
            {
              hasPaymentMethods && paymentMethods.map((paymentMethod) => (
                <AccountPaymentMethodRow
                  key={`${paymentMethod.id}:${paymentMethod.display}`}
                  paymentMethod={paymentMethod}
                  onRemoveCard={this.handleRemoveCard}
                  onDefaultCard={this.handleDefaultCard}
                />
              ))
            }
          </div>
          <div className="AccountPaymentMethods__add-credit-card">
            <Button onClick={this.handleAddCard} sm>
              <TranslationProvider translationKey="add_credit_card">
                <span>Add Credit Card</span>
              </TranslationProvider>
            </Button>
          </div>
          {this.renderAnnouncementTile()}
        </div>
      );
    }
    if (!loading && error) {
      content = (
        <div className="AccountPaymentMethods">
          <TranslationProvider translationKey="communication_error_message">
            <p className="AccountPaymentMethods__subtitle">Error.</p>
          </TranslationProvider>
        </div>
      );
    }

    return (
      <div className={cx('AccountPage', {
        AccountPageWidget: context === APP_CONTEXT.WIDGET,
        AccountPageConsumer: context !== APP_CONTEXT.WIDGET,
        AccountPageAddCardOpen: (modalType === MODAL_ADD_CARD),
      })}
      >
        <TranslationProvider translationKey="payment_methods_title">
          <h2 className="AccountPage__title">Payment Methods</h2>
        </TranslationProvider>
        {content}
        {this.renderModal()}
      </div>
    );
  }
}

AccountPaymentMethods.displayName = 'AccountPaymentMethods';

const mapStateToProps = ({ resyConfig, resyLocations, matchMedia }) => ({
  matchMedia,
  configStatus: resyConfig.configStatus,
  deviceId: resyConfig.deviceId,
  locationCode: resyLocations.selected?.code,
  stripePublishableKey: resyConfig.config?.stripe_publishable_key,
});

export { accountPaymentMethodsPropTypes, AccountPaymentMethods as ComponentUnderTest };
export default connect(mapStateToProps)(AccountPaymentMethods);
